import { forwardRef, type InputHTMLAttributes } from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  showErrorMessage?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, helperText, showErrorMessage = true, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-secondary-700"
          >
            {label}
            {props.required && <span className="ml-1 text-error">*</span>}
          </label>
        )}
        <div className="relative">
          <input
            type={type}
            id={inputId}
            className={cn(
              'flex h-12 w-full rounded-xl border-2 bg-white/50 px-4 py-3 text-base transition-all duration-300',
              'placeholder:text-secondary-400',
              'focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:bg-white',
              'hover:border-primary-300 hover:bg-white',
              'disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:opacity-50',
              error
                ? 'border-error focus:border-error focus:ring-error/20 pr-10'
                : 'border-secondary-200 focus:border-primary-400',
              className
            )}
            ref={ref}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            {...props}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <AlertCircle className="h-5 w-5 text-error" data-testid="input-error-icon" aria-hidden="true" />
            </div>
          )}
          {error && showErrorMessage && (
            <div className="absolute left-0 right-0 top-full z-[100] mt-2">
              <span className="absolute -top-1 left-4 h-2 w-2 rotate-45 bg-error" />
              <p
                id={`${inputId}-error`}
                className="relative block rounded-md bg-error px-3 py-2 text-sm font-medium text-white shadow-md"
              >
                {error}
              </p>
            </div>
          )}
        </div>
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-secondary-500">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
